import React, { ReactNode } from 'react';
import { CoreTooltipPlacement } from './types';
import { Span, Tooltip as ChakraUITooltip } from '@chakra-ui/react';

export interface TooltipProps {
  // Optional so a call site can pair it with `isDisabled` and pass no label when the tooltip is off; an
  // enabled tooltip with no label renders empty content.
  label?: string;
  placement?: CoreTooltipPlacement | undefined;
  isOpen?: boolean;
  hasArrow?: true;
  isDisabled?: boolean;
  children: ReactNode;
}

// CoreTooltip has no DOM node, so props an ancestor's `asChild` clones onto it (e.g. Ark's Menu.Trigger:
// onClick, aria-expanded, ref) are forwarded through Tooltip.Trigger's `asChild` to the real element.
export const CoreTooltip = React.forwardRef<HTMLElement, TooltipProps>(
  ({ label, placement, hasArrow, isOpen, isDisabled, children, ...rest }, ref) => {
    /**
     * DOM
     */
    // A disabled trigger needs no wrapping span: zag wires hover through React's `onPointerMove`, which still
    // fires on a disabled element. asChild renders nothing for a text child, so a string gets a focusable span.
    const trigger =
      typeof children === 'string' ? (
        <Span display="inline-block" tabIndex={0}>
          {children}
        </Span>
      ) : (
        children
      );
    return (
      <ChakraUITooltip.Root open={isOpen} disabled={isDisabled} positioning={{ placement: placement as never }}>
        <ChakraUITooltip.Trigger asChild ref={ref as never} {...rest}>
          {trigger}
        </ChakraUITooltip.Trigger>
        <ChakraUITooltip.Positioner>
          <ChakraUITooltip.Content>
            {hasArrow && (
              <ChakraUITooltip.Arrow>
                <ChakraUITooltip.ArrowTip />
              </ChakraUITooltip.Arrow>
            )}
            {label}
          </ChakraUITooltip.Content>
        </ChakraUITooltip.Positioner>
      </ChakraUITooltip.Root>
    );
  }
);

CoreTooltip.displayName = 'CoreTooltip';
